import {
  lazy,
  Suspense,
  useState,
  type Dispatch,
  type HTMLAttributes,
  type SetStateAction
} from 'react';
import Dropzone, { type DropzoneProps, type FileRejection } from 'react-dropzone';
import { FileTextIcon, UploadIcon } from 'lucide-react';
import { Button } from '@ui/button.tsx';
import { cn, formatBytes } from '@r/utils/utils.ts';
import { useControllableState } from '@r/hooks/use-controllable-state.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@radix-ui/react-icons';
import { ScrollArea } from '@ui/scroll-area.tsx';
import { Progress } from '@ui/progress.tsx';
import type { UploadedFile } from '@r/types/index.ts';
import { isUploadedFileWithPreview } from '@r/utils/utils.ts';
import type { CropAspectOption } from '@r/components/import/image-cropper-dialog.tsx';

// Chargé à la demande : la lib de recadrage (~45 kB) ne pèse ainsi que dans le
// chunk de la modale, pas dans celui de toutes les pages utilisant FileUploader.
const ImageCropperDialog = lazy(() =>
  import('@r/components/import/image-cropper-dialog.tsx').then((module) => ({
    default: module.ImageCropperDialog
  }))
);

interface FileUploaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Value of the uploader.
   * @type File[]
   * @default undefined
   * @example value={files}
   */
  value?: File[];

  /**
   * Function to be called when the value changes.
   * @type Dispatch<SetStateAction<File[]>>
   * @default undefined
   * @example onValueChange={(files) => setFiles(files)}
   */
  onValueChange?: Dispatch<SetStateAction<File[]>>;

  /**
   * Progress of the uploaded files.
   * @type Record<string, number> | undefined
   * @default undefined
   * @example progresses={{ "file1.png": 50 }}
   */
  progresses?: Record<string, number>;

  /**
   * Accepted file types for the uploader.
   * @type { [key: string]: string[]}
   * @default
   * ```ts
   * { "image/*": [] }
   * ```
   * @example accept={["image/png", "image/jpeg"]}
   */
  accept?: DropzoneProps['accept'];

  /**
   * Maximum file size for the uploader.
   * @type number | undefined
   * @default 1024 * 1024 * 2 // 2MB
   * @example maxSize={1024 * 1024 * 2} // 2MB
   */
  maxSize?: DropzoneProps['maxSize'];

  /**
   * Maximum number of files for the uploader.
   * @type number | undefined
   * @default 1
   * @example maxFiles={5}
   */
  maxFiles?: DropzoneProps['maxFiles'];

  /**
   * If the uploader is disabled.
   * @type boolean
   * @default false
   * @example disabled={false}
   */
  disabled?: boolean;

  /**
   * Function to be called when files are parsed.
   * @type (files: File[]) => Promise<unknown>
   * @default undefined
   * @example onParse={(files) => parseFiles(files)}
   */
  onParse?: (files: File[]) => Promise<unknown>;

  /**
   * Uploaded files.
   * @type UploadedFile[]
   * @default []
   * @example uploadedFiles={[{ name: 'example.png', size: 1000, type: 'image/png', data: 'base64 data', iri: 'https://example.com/example.png' }]}
   */
  uploadedFiles?: UploadedFile[];

  /**
   * Called when the user removes an already uploaded file (from `uploadedFiles`).
   */
  onUploadedFileRemove?: () => void;

  /**
   * Si défini, ouvre une modale de recadrage avant l'ajout de toute image déposée.
   * `number` = ratio fixe. Tableau = l'utilisateur choisit le ratio.
   * Sans effet sur les dépôts multiples.
   * @default undefined
   * @example cropAspect={2}
   * @example cropAspect={[{ label: 'Paysage', value: 3 }, { label: 'Carré', value: 1 }]}
   */
  cropAspect?: number | CropAspectOption[];
}

export const FileUploader = (props: FileUploaderProps) => {
  const { t } = useTranslation();

  const {
    accept = {},
    maxFiles = 1,
    value: valueProp,
    progresses,
    uploadedFiles,
    onUploadedFileRemove,
    onValueChange,
    disabled = false,
    maxSize,
    onParse,
    cropAspect
  } = props;

  const [files, setFiles] = useControllableState({
    prop: valueProp,
    onChange: onValueChange
  });

  const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);

  const reportRejectedFiles = (rejectedFiles: FileRejection[]) => {
    for (const { file, errors } of rejectedFiles) {
      toast.error(t('file.upload.error.file.rejected', { fileName: file.name }));
      console.error(errors);
    }
  };

  const commitFiles = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file)
      })
    );

    const updatedFiles = files ? [...files, ...newFiles] : newFiles;

    setFiles(updatedFiles);
    reportRejectedFiles(rejectedFiles);

    if (onParse && updatedFiles.length > 0) {
      onParse(updatedFiles).finally(() => {
        setFiles([]);
      });
    }
  };

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (maxFiles === 1 && acceptedFiles.length > 1) {
      toast.error(t('file.upload.error.multiple.files'));
      return;
    }

    if ((files?.length ?? 0) + acceptedFiles.length > maxFiles) {
      toast.error(t('file.upload.error.max.files', { maxFiles }));
      return;
    }

    if (maxSize && acceptedFiles.some((file) => file.size > maxSize)) {
      toast.error(t('file.upload.error.file.size', { maxSize: formatBytes(maxSize) }));
      return;
    }

    const isSingleImage = acceptedFiles.length === 1 && acceptedFiles[0]?.type.startsWith('image/');

    if (cropAspect && isSingleImage) {
      setPendingCropFile(acceptedFiles[0]);
      reportRejectedFiles(rejectedFiles);
      return;
    }

    commitFiles(acceptedFiles, rejectedFiles);
  };

  function onRemove(index: number) {
    if (!files) return;
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onValueChange?.(newFiles);
  }

  const disabledDropzone = disabled || (uploadedFiles?.length ?? 0) >= maxFiles;

  return (
    <div className="p-1">
      {cropAspect && pendingCropFile ? (
        <Suspense fallback={null}>
          <ImageCropperDialog
            file={pendingCropFile}
            aspect={cropAspect}
            onCancel={() => setPendingCropFile(null)}
            onConfirm={(croppedFile) => {
              setPendingCropFile(null);
              commitFiles([croppedFile], []);
            }}
            onUploadOriginal={(originalFile) => {
              setPendingCropFile(null);
              commitFiles([originalFile], []);
            }}
          />
        </Suspense>
      ) : null}
      <div className="pt-6">
        <div className="flex overflow-hidden relative flex-col gap-6">
          <Dropzone onDrop={onDrop} accept={accept} disabled={disabledDropzone}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                className={cn(
                  'group relative grid h-52 w-full cursor-pointer place-items-center rounded-lg border-2 border-muted-foreground/25 border-dashed px-5 py-2.5 text-center transition hover:bg-muted/25',
                  'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isDragActive && 'border-muted-foreground/50',
                  disabled && 'pointer-events-none opacity-60'
                )}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <div className="flex flex-col gap-4 justify-center items-center w-full">
                    <div className="p-3 rounded-full border border-dashed bg-primary">
                      <UploadIcon className="size-7 text-primary-foreground" aria-hidden="true" />
                    </div>
                    <div className="text-2xl text-muted-foreground">
                      {t('file.upload.drag.label')}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 justify-center items-center w-full">
                    <div className="text-2xl text-muted-foreground">{t('file.upload.label')}</div>
                    <Button rounded="full" type="button" disabled={disabledDropzone}>
                      {t('file.upload.button')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Dropzone>
          {files?.length ? (
            <>
              <span className="block font-medium text-foreground pl-[0.125rem]">
                {t('file.upload.files.to.upload')}
              </span>

              <ScrollArea className="w-full h-fit">
                <div className="flex flex-col gap-2 max-h-48">
                  {files?.map((file, index) => (
                    <FileCard
                      key={index}
                      file={file}
                      onRemove={() => onRemove(index)}
                      progress={progresses?.[file.name]}
                    />
                  ))}
                </div>
              </ScrollArea>
            </>
          ) : null}
          {uploadedFiles && uploadedFiles?.length > 0 ? (
            <>
              <span className="block font-medium text-foreground pl-[0.125rem]">
                {t('file.upload.uploaded.files')}
              </span>
              <ScrollArea className="w-full h-fit">
                <div className="flex flex-col gap-2 max-h-48">
                  {uploadedFiles.map((file, index) => (
                    <UploadedFileCard
                      key={index}
                      file={file}
                      disabled={disabled}
                      onRemove={() => onUploadedFileRemove?.()}
                    />
                  ))}
                </div>
              </ScrollArea>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface FileCardProps {
  file: File;
  onRemove: () => void;
  progress?: number;
}

function FileCard({ file, progress, onRemove }: FileCardProps) {
  const { t } = useTranslation();

  return (
    <div className="relative flex items-start gap-2.5 border p-2 rounded-md">
      <div className="flex min-w-0 flex-1 gap-2.5">
        {isFileWithPreview(file) ? <FilePreview file={file} /> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-col gap-px">
            <p className="break-all text-sm font-medium leading-snug text-foreground/80">
              {file.name}
            </p>
            <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          {progress ? <Progress value={progress} /> : null}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 items-center">
        <Button type="button" variant="outline" size="icon" className="size-7" onClick={onRemove}>
          <Cross2Icon className="size-4" aria-hidden="true" />
          <span className="sr-only">{t('file.upload.remove.file')}</span>
        </Button>
      </div>
    </div>
  );
}

function isFileWithPreview(file: File): file is File & { preview: string } {
  return 'preview' in file && typeof file.preview === 'string';
}

interface FilePreviewProps {
  file: File & { preview: string };
}

function FilePreview({ file }: FilePreviewProps) {
  if (file.type.startsWith('image/')) {
    return (
      <img
        src={file.preview}
        alt={file.name}
        width={48}
        height={48}
        loading="lazy"
        className="object-cover rounded-md aspect-square shrink-0"
      />
    );
  }

  return <FileTextIcon className="size-10 text-muted-foreground" aria-hidden="true" />;
}

function UploadedFilePreview({ file }: { file: UploadedFile }) {
  return (
    <img
      src={file.pathUri}
      alt={file.name}
      width={48}
      height={48}
      loading="lazy"
      className="object-cover rounded-md aspect-square shrink-0"
    />
  );
}

interface UploadedFileCardProps {
  file: UploadedFile;
  onRemove?: () => void;
  disabled?: boolean;
}

function UploadedFileCard({ file, onRemove, disabled }: UploadedFileCardProps) {
  const { t } = useTranslation();

  return (
    <div className="relative flex items-start gap-2.5 border p-2 rounded-md">
      <div className="flex min-w-0 flex-1 gap-2.5">
        {isUploadedFileWithPreview(file) ? <UploadedFilePreview file={file} /> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-col gap-px">
            <p className="break-all text-sm font-medium leading-snug text-foreground/80">
              {file.name}
            </p>
          </div>
        </div>
      </div>
      {onRemove ? (
        <div className="flex shrink-0 gap-2 items-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-7"
            disabled={disabled}
            onClick={onRemove}
          >
            <Cross2Icon className="size-4" aria-hidden="true" />
            <span className="sr-only">{t('file.upload.remove.file')}</span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
